﻿import { motion } from 'framer-motion';
import { X, Minimize2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { WindowItem } from '@/hooks/useWindows';
import type { ComponentType } from 'react';

type TaskbarPreviewProps = {
  appId: string;
  appTitle: string;
  instances: WindowItem[];
  Icon?: ComponentType<{ className?: string }>;
  activeId: string | null;
  onFocus: (windowId: string) => void;
  onClose: (windowId: string) => void;
  onMinimize: (windowId: string) => void;
  buttonRect: DOMRect;
  onHoverEnter?: () => void;
  onHoverLeave?: () => void;
};

export function TaskbarPreview({
  appTitle,
  instances,
  Icon,
  activeId,
  onFocus,
  onClose,
  onMinimize,
  buttonRect,
  onHoverEnter,
  onHoverLeave,
}: TaskbarPreviewProps) {
  const { t } = useTranslation();
  // Berechne Position fÃ¼r das Vorschau-Popup
  const previewWidth = 280;
  const previewLeft = Math.max(
    10,
    Math.min(
      buttonRect.left + buttonRect.width / 2 - previewWidth / 2,
      window.innerWidth - previewWidth - 10
    )
  );
  return (
    <div
      className="fixed z-[9999] pointer-events-auto"
      style={{
        left: previewLeft,
        bottom: 0,
        width: previewWidth,
        paddingBottom: window.innerHeight - buttonRect.bottom,
      }}
      onMouseEnter={onHoverEnter}
      onMouseLeave={onHoverLeave}
    >
      <motion.div
        initial={{ opacity: 0, y: 10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.95 }}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
      >
      <div className="taskbar-preview-panel">
        {/* Header */}
        <div className="taskbar-preview__header">
          <div className="taskbar-preview__header-content">
            {Icon && <Icon className="taskbar-preview__header-icon" />}
            <span className="taskbar-preview__header-title">
              {appTitle}
            </span>
            {instances.length > 1 && (
              <span className="taskbar-preview__header-count">
                {instances.length}
              </span>
            )}
          </div>
        </div>

        {/* Kompakte Liste */}
        <div className="taskbar-preview__list">
          {instances.map((instance, index) => {
            const isActive = activeId === instance.id && !instance.minimized;

            return (
              <motion.div
                key={instance.id}
                className={cn(
                  'taskbar-preview__item',
                  isActive && 'taskbar-preview__item--active',
                  instance.minimized && 'taskbar-preview__item--minimized'
                )}
                onClick={() => onFocus(instance.id)}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                {/* Nummer */}
                <div className="taskbar-preview__badge">
                  <span className="taskbar-preview__badge-label">
                    {index + 1}
                  </span>
                </div>

                {/* Icon */}
                {Icon && (
                  <div className="taskbar-preview__item-icon">
                    <Icon className="taskbar-preview__item-icon-svg" />
                  </div>
                )}

                {/* Titel & Status */}
                <div className="taskbar-preview__meta">
                  <div className="taskbar-preview__title">
                    {instance.title}
                  </div>
                  {instance.subtitle && (
                    <div className="taskbar-preview__subtitle">
                      {instance.subtitle}
                    </div>
                  )}
                  <div className="taskbar-preview__status">
                    {isActive && (
                      <span className="taskbar-preview__status-chip taskbar-preview__status-chip--active">
                        <div className="taskbar-preview__status-dot" />
                        {t('taskbar.preview.active')}
                      </span>
                    )}
                    {instance.minimized && (
                      <span className="taskbar-preview__status-chip">
                        <Minimize2 className="taskbar-preview__status-icon" />
                        {t('taskbar.preview.minimized')}
                      </span>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="taskbar-preview__actions">
                  {/* Minimize Button */}
                  <button
                    className="taskbar-preview__action"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMinimize(instance.id);
                    }}
                    title={instance.minimized ? t('taskbar.preview.restore') : t('taskbar.preview.minimize')}
                    aria-label={instance.minimized ? `${t('taskbar.preview.restore')} ${instance.title}` : `${t('taskbar.preview.minimize')} ${instance.title}`}
                  >
                    <Minimize2 className="taskbar-preview__action-icon" />
                  </button>

                  {/* Close Button */}
                  <button
                    className="taskbar-preview__action taskbar-preview__action--danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose(instance.id);
                    }}
                    title={t('taskbar.preview.close')}
                    aria-label={`${t('taskbar.preview.close')} ${instance.title}`}
                  >
                    <X className="taskbar-preview__action-icon" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Pfeil zum Taskbar-Button */}
      <div className="taskbar-preview__caret" />
      </motion.div>
    </div>
  );
}





